import { authorStorageHeaders } from '../../services/authorStorage';
import { displayLabel } from "../../utils/displayLabel";
// BookDetails.js — EDIT PAGE with eBook upload (matches AddBook behavior for eBooks + genres)
// TailwindCSS UI; lucide-react optional.

import React, { useMemo, useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { fetchUserAttributes, getCurrentUser } from "@aws-amplify/auth";
import { Plus, Upload, CheckCircle2, AlertCircle } from "lucide-react";

/* ------------------- Canonical Genre Tree (same as AddBook.js) ------------------- */
const GENRE_TREE = {
  Fiction: {
    main: [
      "Children's Books",
      "Fantasy",
      "Romantasy",
      "Science Fiction",
      "Horror",
      "Mystery / Crime",
      "Thriller / Suspense",
      "Romance",
      "Historical Fiction",
      "Western",
      "Literary Fiction",
    ],
    sub: {
      "Children's Books": ["Picture Books", "Middle Grade"],
      Fantasy: ["Dark Fantasy", "High / Epic Fantasy", "Urban Fantasy", "Fairytales & Folklore", "Wizards & Witches"],
      Romantasy: ["Dark Romantasy", "Fae Romantasy", "Paranormal Romantasy"],
      "Science Fiction": ["Space Opera", "Dystopian", "Cyberpunk", "Steampunk"],
      Horror: ["Gothic Horror", "Paranormal", "Psychological Horror"],
      "Mystery / Crime": ["Detective", "Cozy Mystery", "Police Procedural"],
      "Thriller / Suspense": ["Spy Thriller", "Political Thriller", "Psychological Thriller"],
      Romance: ["Contemporary Romance", "Historical Romance", "Paranormal Romance"],
      "Historical Fiction": ["War Stories", "Biographical Fiction"],
      Western: ["Classic Western", "Weird West"],
      "Literary Fiction": [],
    },
  },
  "Non-Fiction": {
    main: [
      "Biography / Memoir",
      "History",
      "Self-Help / Personal Development",
      "Religion / Spirituality",
      "Science / Nature",
      "Travel / Adventure",
      "True Crime",
      "Essays",
    ],
    sub: {
      "Biography / Memoir": ["Celebrity Memoir", "Literary Memoir"],
      History: ["Military History", "Cultural History"],
      "Self-Help / Personal Development": ["Habits", "Productivity", "Relationships"],
      "Religion / Spirituality": ["Inspirational", "Comparative Religion"],
      "Science / Nature": ["Popular Science", "Environment"],
      "Travel / Adventure": ["Travelogue", "Exploration"],
      "True Crime": ["Case Study", "Investigative"],
      Essays: [],
    },
  },
};

/* ------------------- Existing config (match AddBook.js) ------------------- */
const formatOptions = ["Paperback", "Hardcover", "Special Edition", "eBook"];

const COVER_PRESIGN_URL =
  "https://hiuyw6ij1d.execute-api.us-west-2.amazonaws.com/default/generate-presigned-url";

// ✅ eBook presign (same one used in AddBook)
const EBOOK_PRESIGN_URL =
  "https://o27abx1bhl.execute-api.us-west-2.amazonaws.com/default/presignEbook";

const SAVE_URL =
  "https://yg2oooqsm0.execute-api.us-west-2.amazonaws.com/default/savebookdata";

const S3_REGION = "us-west-2";
const BUCKET_NAME = "authoruser67c1f-dev";

const PLACEHOLDER = "/images/book-cover-placeholder.svg";
const LIST_URL = "https://h9yz2f8v47.execute-api.us-west-2.amazonaws.com/default/fetchbooksforauthor";

const objectUrl = (bucket, key) =>
  `https://${bucket}.s3.${S3_REGION}.amazonaws.com/${key.split("/").map(encodeURIComponent).join("/")}`;

/* ---------- Cover resolver ---------- */
function resolveCoverUrl(format) {
  if (format?.displayCoverUrl) return format.displayCoverUrl;
  if (format?.coverImageUrl) return format.coverImageUrl;
  const last =
    Array.isArray(format?.coverImages) && format.coverImages.length
      ? format.coverImages[format.coverImages.length - 1]
      : null;
  if (last?.url) return last.url;
  if (last?.masterBucket && last?.masterKey) return objectUrl(last.masterBucket, last.masterKey);
  if (last?.authorBucket && last?.authorKey) return objectUrl(last.authorBucket, last.authorKey);
  return "";
}

function resolveCoverKey(format) {
  if (format?.coverKey) return format.coverKey;
  const last =
    Array.isArray(format?.coverImages) && format.coverImages.length
      ? format.coverImages[format.coverImages.length - 1]
      : null;
  return last?.masterKey || last?.authorKey || "";
}

/* ---------- Helpers ---------- */
const dedupeLower = (arr) => {
  const seen = new Set();
  const out = [];
  for (const v of arr || []) {
    const k = String(v).toLowerCase();
    if (!seen.has(k)) {
      seen.add(k);
      out.push(v);
    }
  }
  return out;
};

const clamp = (arr, n) => (Array.isArray(arr) ? arr.slice(0, n) : []);

const toKeySafe = (text = "") =>
  String(text)
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/['"]/g, "")
    .replace(/[^A-Za-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .trim() || "unknown";

const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
};

const createExternalLink = () => ({ label: "", url: "" });
const MAX_VARIATION_IMAGES = 3;
const createDefaultVariation = () => ({
  name: "",
  price: "",
  extrainfo: "",
  quantity: "",
  useBaseQuantity: true,
  images: [],
});
const createDefaultShipping = () => ({
  weight: "",
  length: "",
  width: "",
  height: "",
  mediaMailEligible: false,
  hasExtras: false,
  freeShipping: false,
});

const normalizeShipping = (shipping = {}) => {
  const normalized = {
    ...createDefaultShipping(),
    ...(shipping || {}),
    freeShipping: shipping?.freeShipping === true,
  };

  if (normalized.hasExtras) normalized.mediaMailEligible = false;
  return normalized;
};

const shouldShowShipping = (format = {}) =>
  format.type !== "eBook" && !!format.isDirect;

const hasValidDirectShipping = (format = {}) => {
  if (!shouldShowShipping(format)) return true;

  const shipping = normalizeShipping(format.shipping);
  const fields = ["weight", "length", "width", "height"];

  return fields.every((field) => {
    const value = Number(shipping[field]);
    return Number.isFinite(value) && value > 0;
  });
};

const coerceBoolean = (value, fallback = false) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "true") return true;
    if (normalized === "false") return false;
  }
  if (typeof value === "number") {
    if (value === 1) return true;
    if (value === 0) return false;
  }
  return fallback;
};

const getFormatType = (format = {}) =>
  format.type || format.format || format.formatKey || "Paperback";

const isValidUrl = (value = "") => {
  try {
    const url = new URL(value);
    return ["http:", "https:"].includes(url.protocol);
  } catch {
    return false;
  }
};

const normalizeExternalLinks = (format = {}) => {
  if (Array.isArray(format.externalLinks)) {
    return format.externalLinks.map((link) => ({
      label: link?.label || "",
      url: link?.url || "",
    }));
  }

  const legacyUrl = format.linkUrl || format.url || "";
  if (!legacyUrl) return [];

  return [{
    label: format.linkText || "",
    url: legacyUrl,
  }];
};

const hasPersistedEbookAsset = (format = {}) => {
  const persistedFilename = typeof format.ebookFilename === "string" ? format.ebookFilename.trim() : "";

  return Boolean(
    format.ebookFileUrl ||
      format.ebookS3Key ||
      format.ebookKey ||
      (!format.ebookFile && persistedFilename)
  );
};

const normalizeFormatForState = (format = {}) => {
  const type = getFormatType(format);
  const isEbook = type === "eBook";
  const coverImages = Array.isArray(format.coverImages) ? format.coverImages : [];
  const hasExistingEbookAsset = hasPersistedEbookAsset(format);
  const fulfillmentType = String(format.fulfillmentType || "").toLowerCase();
  const directFromFulfillment =
    fulfillmentType === "direct" ? true : fulfillmentType === "external" ? false : undefined;
  const directFlagFallback = format.isDirect == null && typeof directFromFulfillment === "undefined" ? !isEbook : false;

  const variations = Array.isArray(format.variations)
    ? format.variations.map((variation) => ({
        name: variation?.name || "",
        extrainfo: variation?.extrainfo || "",
        price: String(variation?.price ?? ""),
        quantity: String(variation?.quantity ?? ""),
        useBaseQuantity: coerceBoolean(variation?.useBaseQuantity ?? variation?.useBaseInventory, true),
        unlimited: variation?.quantity === null || variation?.inventoryPolicy === "infinite",
        images: (Array.isArray(variation?.images) ? variation.images : [])
        .slice(0, MAX_VARIATION_IMAGES)
        .map((image) => ({
          url: image?.url || "",
          filename: image?.filename || image?.fileName || "",
          authorUrl: image?.authorUrl || "",
          authorBucket: image?.authorBucket || "",
          authorKey: image?.authorKey || "",
          masterBucket: image?.masterBucket || "",
          masterKey: image?.masterKey || image?.key || "",
          key: image?.key || image?.masterKey || "",
        }))
        .filter((image) => image.url),
      }))
    : [];

  return {
    type,
    extrainfo: format.extrainfo || "",
    price: String(format.price ?? ""),
    isFree: coerceBoolean(format.isFree, false),
    quantity: isEbook ? "" : String(format.quantity ?? ""),
    unlimited: format.quantity === null || format.inventoryPolicy === "infinite",
    displayCoverUrl: format.displayCoverUrl || "",
    coverImages,
    coverImageUrl: format.coverImageUrl || "",
    coverKey: format.coverKey || resolveCoverKey(format) || "",
    isDirect: format.isDirect == null
      ? typeof directFromFulfillment === "boolean"
        ? directFromFulfillment
        : directFlagFallback
      : coerceBoolean(format.isDirect, directFlagFallback),
    externalLinks: normalizeExternalLinks(format),
    ebookFileUrl: format.ebookFileUrl || "",
    ebookS3Key: format.ebookS3Key || format.ebookKey || "",
    ebookKey: format.ebookKey || format.ebookS3Key || "",
    ebookFilename: format.ebookFilename || "",
    ebookFilesize: format.ebookFilesize || 0,
    ebookMime: format.ebookMime || "",
    ebookFile: null,
    ebookUploaded: hasExistingEbookAsset,
    shipping: normalizeShipping(format.shipping),
    variations,
    showVariations: variations.length > 0,
  };
};

const createDefaultFormat = () => ({
  type: "Paperback",
  price: "",
  extrainfo: "",
  isFree: false,
  quantity: "",
  coverImages: [],
  coverImageUrl: "",
  coverKey: "",
  isDirect: true,
  externalLinks: [],
  ebookFileUrl: "",
  ebookS3Key: "",
  ebookKey: "",
  ebookFilename: "",
  ebookFilesize: 0,
  ebookMime: "",
  ebookFile: null,
  ebookUploaded: false,
  shipping: createDefaultShipping(),
  variations: [],
  showVariations: false,
});

const buildSharedImageFormats = (formats = []) => {
  const source =
    formats.find((format) => resolveCoverUrl(format) || resolveCoverKey(format)) || formats[0];

  if (!source) return formats;

  const sharedCoverImageUrl = source.coverImageUrl || "";
  const sharedCoverKey = source.coverKey || resolveCoverKey(source) || "";
  const sharedCoverImages = Array.isArray(source.coverImages) ? source.coverImages : [];

  return formats.map((format) => ({
    ...format,
    coverImageUrl: sharedCoverImageUrl || format.coverImageUrl || "",
    displayCoverUrl: source.displayCoverUrl || "",
    coverKey: sharedCoverKey || format.coverKey || "",
    coverImages: sharedCoverImages.length ? sharedCoverImages : format.coverImages || [],
  }));
};

const buildInitialFormats = (book = {}) => {
  const nestedFormats = Array.isArray(book.formats) ? book.formats : [];
  const topLevelFormat =
    book?.format || book?.formatKey || book?.type
      ? {
          ...book,
          type: getFormatType(book),
        }
      : null;

  const sources = topLevelFormat ? [...nestedFormats, topLevelFormat] : nestedFormats;

  if (!sources.length) return [createDefaultFormat()];

  const merged = new Map();

  sources.forEach((source, index) => {
    const type = getFormatType(source);
    const key = `${type}`.toLowerCase() || `format-${index}`;
    const previous = merged.get(key) || {};
    merged.set(key, {
      ...previous,
      ...source,
      type,
      externalLinks:
        Array.isArray(source.externalLinks) && source.externalLinks.length > 0
          ? source.externalLinks
          : previous.externalLinks || [],
      coverImages:
        Array.isArray(source.coverImages) && source.coverImages.length > 0
          ? source.coverImages
          : previous.coverImages || [],
    });
  });

  return Array.from(merged.values()).map((format) => normalizeFormatForState(format));
};

const putToPresigned = async (file, signed, contentType) => {
  if (!signed?.url) throw new Error("Missing presigned URL");
  // Detect if SSE header is required
  let needsSSE = false;
  try {
    const q = signed.url.split("?")[1] || "";
    const params = new URLSearchParams(q);
    const signedHeaders = decodeURIComponent(params.get("X-Amz-SignedHeaders") || "");
    needsSSE = signedHeaders.toLowerCase().includes("x-amz-server-side-encryption");
  } catch {}
  const headers = { "Content-Type": contentType || file.type || "application/octet-stream" };
  if (needsSSE) headers["x-amz-server-side-encryption"] = "AES256";

  const r = await fetch(signed.url, { method: "PUT", headers, body: file });
  if (!r.ok) throw new Error(`Upload failed: ${r.status}`);
  return { bucket: signed.bucket, key: signed.key, url: objectUrl(signed.bucket, signed.key) };
};

/* ------------------- Component ------------------- */
const BookDetails = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const title = state?.book?.title;
  const [book, setBook] = useState(null);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let cancelled = false;
    if (!title) return undefined;
    setBook(null); setError('');
    (async () => {
      try {
        const response = await fetch(`${LIST_URL}?book=${encodeURIComponent(title)}`, { headers: await authorStorageHeaders(), cache: 'no-store' });
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Your book could not be loaded.');
        const saved = data.books?.find(item => item.title === title);
        if (!saved?.version) throw new Error('This book is unavailable. Return to My Books and choose a saved book.');
        if (!cancelled) setBook(saved);
      } catch (e) { if (!cancelled) setError(e.message || 'Your book could not be loaded.'); }
    })();
    return () => { cancelled = true; };
  }, [title, attempt]);
  if (!title || error) return <section className="p-6 text-bookish-purple"><p role="alert">{error || 'Choose a saved book from My Books to edit.'}</p>{title && <button className="min-h-[44px] underline mr-4" onClick={() => setAttempt(n => n + 1)}>Retry loading book</button>}<button className="min-h-[44px] underline" onClick={() => navigate('/authorportal/mainpage?tab=books')}>Back to My Books</button></section>;
  if (!book) return <p role="status" className="p-6">Loading your saved book…</p>;
  return <BookDetailsEditor key={book.version + attempt} book={book} reload={() => setAttempt(n => n + 1)} />;
};

const BookDetailsEditor = ({ book, reload }) => {
  const navigate = useNavigate();
  const initialCategory = book.category && GENRE_TREE[book.category] ? book.category : "Fiction";
  const initialMain = dedupeLower(clamp(book.genres || [], 3));
  const initialSub = dedupeLower(book.subgenres || []);

  const [bookData, setBookData] = useState({
    title: book.title || "",
    tagline: book.tagline || "",
    blurb: book.blurb || "",
    extrainfo: book.extrainfo || "",
    public: Boolean(book.public),
    series: book.series || "",
    bookNumber: String(book.bookNumber ?? ""),
  });

  const [isSeries, setIsSeries] = useState(Boolean(book.series || book.bookNumber));

  // Genres (identical semantics to AddBook)
  const [category, setCategory] = useState(initialCategory);
  const [mainGenres, setMainGenres] = useState(initialMain);
  const [selectedMainForSubs, setSelectedMainForSubs] = useState(initialMain[0] || "");
  const [subgenres, setSubgenres] = useState(initialSub);

  // Formats — preserve any existing ebook fields
  const hasInitialFormatData =
    (Array.isArray(book.formats) && book.formats.length > 0) ||
    Boolean(book?.format || book?.formatKey || book?.type);

  const [formats, setFormats] = useState(
    hasInitialFormatData ? buildInitialFormats(book) : [createDefaultFormat()]
  );
  const [useSameImage, setUseSameImage] = useState(false);
  const [saveFeedback, setSaveFeedback] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const hasInvalidDirectShipping = useMemo(
    () => formats.some((format) => !hasValidDirectShipping(format)),
    [formats]
  );
  
  const [busy, setBusy] = useState('');
  const [canResume, setCanResume] = useState(false);
  const [dirty, setDirty] = useState(false);
  const lock = useRef(false);
  const feedbackRef = useRef(null);
  const formRef = useRef(null);
  const labelId = useRef(0);
  const begin = label => { if (lock.current) return false; lock.current = true; setBusy(label); setSaveFeedback(null); return true; };
  const end = () => { lock.current = false; setBusy(''); setIsSaving(false); };
  const report = (message, type = 'error') => { setSaveFeedback({ message, type }); setTimeout(() => feedbackRef.current?.focus(), 0); };
  const leave = () => { if (!dirty || window.confirm('Discard your unsaved book changes?')) navigate('/authorportal/mainpage?tab=books'); };
  const refresh = () => { if (!dirty || window.confirm('Reload this book and discard unsaved changes?')) reload(); };
  useEffect(() => {
    const closing = e => { if (dirty || lock.current) { e.preventDefault(); e.returnValue = ''; } };
    const tabChange = e => { if (lock.current || (dirty && !window.confirm('Discard your unsaved book changes?'))) e.preventDefault(); };
    window.addEventListener('beforeunload', closing);
    window.addEventListener('bookish:before-navigation', tabChange);
    return () => { window.removeEventListener('beforeunload', closing); window.removeEventListener('bookish:before-navigation', tabChange); };
  }, [dirty]);
  // Associate the existing form's visible labels with their controls, including
  // dynamically added format, variation and retailer fields.
  useEffect(() => {
    formRef.current?.querySelectorAll('label').forEach(label => {
      if (label.htmlFor || label.querySelector('input,select,textarea')) return;
      const next = label.nextElementSibling;
      const control = next?.matches('input,select,textarea') ? next : next?.querySelector('input,select,textarea');
      if (control && !control.id) control.id = `book-detail-${labelId.current++}`;
      if (control) label.htmlFor = control.id;
    });
  });
  const resume = async () => {
    if (!begin('Finishing previous operation…')) return;
    try {
      const response = await axios.post(SAVE_URL, { action: 'resume' }, { headers: await authorStorageHeaders() });
      if (!response.data?.saved) throw new Error('The operation could not be confirmed.');
      setCanResume(false);
      report('The previous operation is complete. Reload this book or return to My Books to see the result. Your current entries remain here.', 'success');
    } catch (e) { report(e.response?.data?.error || e.message); }
    finally { end(); }
  };

  /* ---------- Basic handlers ---------- */
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setBookData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const onChangeCategory = (next) => {
    setCategory(next);
    setMainGenres([]);
    setSubgenres([]);
    setSelectedMainForSubs("");
  };

  const availableMain = GENRE_TREE[category].main;
  const subOptions = useMemo(
    () => (selectedMainForSubs ? GENRE_TREE[category].sub[selectedMainForSubs] || [] : []),
    [category, selectedMainForSubs]
  );

  const addMainGenre = (g) => {
    if (!g) return;
    if (mainGenres.includes(g)) {
      setSelectedMainForSubs(g);
      return;
    }
    if (mainGenres.length >= 3) return;
    const next = clamp(dedupeLower([...mainGenres, g]), 3);
    setMainGenres(next);
    setSelectedMainForSubs(g);
  };

  const removeMainGenre = (g) => {
    const next = mainGenres.filter((x) => x !== g);
    setMainGenres(next);
    if (selectedMainForSubs === g) setSelectedMainForSubs(next[0] || "");
    const removedSubs = new Set((GENRE_TREE[category].sub[g] || []).map((s) => s.toLowerCase()));
    setSubgenres((prev) => prev.filter((sg) => !removedSubs.has(String(sg).toLowerCase())));
  };

  const addSubgenre = (sg) => {
    if (!sg) return;
    if (!mainGenres.includes(selectedMainForSubs)) return;
    const valid = new Set((GENRE_TREE[category].sub[selectedMainForSubs] || []).map((s) => s.toLowerCase()));
    if (!valid.has(String(sg).toLowerCase())) return;
    setSubgenres((prev) => dedupeLower([...prev, sg]));
  };

  const removeSubgenre = (sg) => {
    setSubgenres((prev) => prev.filter((x) => x !== sg));
  };

  const handleFormatChange = (index, field, value) => {
    setFormats((prev) => {
      const updated = [...prev];
      const item = { ...updated[index] };

      if (field === "type") {
        item.type = value;
        if (value !== "eBook") {
          item.isFree = false;
          item.ebookFile = null;
          item.ebookFilename = "";
          item.ebookFilesize = 0;
          item.ebookMime = "";
          item.ebookS3Key = "";
          item.ebookKey = "";
          item.ebookUploaded = false;
        }
        if (value === "eBook") {
          const hasEbookFile = hasPersistedEbookAsset(item) || !!item.ebookUploaded;
          item.isDirect = hasEbookFile ? item.isDirect : false;
          item.quantity = "";
        }
        updated[index] = item;
        return updated;
      }

      if (field === "isFree") {
        const isFree = !!value;
        item.isFree = isFree;
        item.price = isFree ? "0.00" : item.price === "0.00" ? "" : item.price ?? "";
        updated[index] = item;
        return updated;
      }

      if (field === "price") {
        item.price = item.isFree ? "0.00" : value;
        updated[index] = item;
        return updated;
      }

      if (field === "isDirect") {
        const hasEbookFile = hasPersistedEbookAsset(item) || !!item.ebookUploaded;
        item.isDirect = item.type === "eBook" && !hasEbookFile ? false : !!value;
        if (!item.isDirect || item.type === "eBook") {
          item.quantity = "";
          item.showVariations = false;
        }
        updated[index] = item;
        return updated;
      }

      if (field === "quantity" && item.type === "eBook") {
        item.quantity = "";
        updated[index] = item;
        return updated;
      }

      if (field.startsWith("shipping.")) {
        const shippingField = field.split(".")[1];
        const shipping = normalizeShipping(item.shipping);
        shipping[shippingField] = value;

        if (shippingField === "hasExtras" && value) {
          shipping.mediaMailEligible = false;
        }
        if (shippingField === "mediaMailEligible" && shipping.hasExtras) {
          shipping.mediaMailEligible = false;
        }

        item.shipping = shipping;
        updated[index] = item;
        return updated;
      }

      updated[index] = { ...item, [field]: value };
      return updated;
    });
  };

  const handleExternalLinkChange = (formatIndex, linkIndex, field, value) => {
    setFormats((prev) =>
      prev.map((format, idx) =>
        idx === formatIndex
          ? {
              ...format,
              externalLinks: (format.externalLinks || []).map((link, currentLinkIndex) =>
                currentLinkIndex === linkIndex ? { ...link, [field]: value } : link
              ),
            }
          : format
      )
    );
  };

  const addExternalLink = (formatIndex) => {
    setFormats((prev) =>
      prev.map((format, idx) =>
        idx === formatIndex
          ? { ...format, externalLinks: [...(format.externalLinks || []), createExternalLink()] }
          : format
      )
    );
  };

  const removeExternalLink = (formatIndex, linkIndex) => {
    setFormats((prev) =>
      prev.map((format, idx) =>
        idx === formatIndex
          ? {
              ...format,
              externalLinks: (format.externalLinks || []).filter((_, currentLinkIndex) => currentLinkIndex !== linkIndex),
            }
          : format
      )
    );
  };

  const addFormat = () => {
    if (formats.length >= 4) return;
    setFormats((prev) => [...prev, createDefaultFormat()]);
  };

  const removeFormat = (index) => {
    setFormats((prev) => prev.filter((_, idx) => idx !== index));
  };

  const toggleVariationsSection = (formatIndex) => {
    setFormats((prev) =>
      prev.map((format, idx) =>
        idx === formatIndex ? { ...format, showVariations: !format.showVariations } : format
      )
    );
  };

  const addVariation = (formatIndex) => {
    setFormats((prev) =>
      prev.map((format, idx) =>
        idx === formatIndex
          ? { ...format, showVariations: true, variations: [...(format.variations || []), createDefaultVariation()] }
          : format
      )
    );
  };

  const removeVariation = (formatIndex, variationIndex) => {
    setFormats((prev) =>
      prev.map((format, idx) =>
        idx === formatIndex
          ? {
              ...format,
              variations: (format.variations || []).filter((_, currentIndex) => currentIndex !== variationIndex),
            }
          : format
      )
    );
  };

  const handleVariationChange = (formatIndex, variationIndex, field, value) => {
    setFormats((prev) =>
      prev.map((format, idx) => {
        if (idx !== formatIndex) return format;
        return {
          ...format,
          variations: (format.variations || []).map((variation, currentIndex) => {
            if (currentIndex !== variationIndex) return variation;
            if (field === "useBaseQuantity") {
              return {
                ...variation,
                useBaseQuantity: !!value,
                quantity: value ? "" : variation.quantity,
              };
            }
            return { ...variation, [field]: value };
          }),
        };
      })
    );
  };

  const uploadCoverImageAsset = async ({ file, formatType }) => {
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type) || file.size > 10 * 1024 * 1024 || !file.size) throw new Error('Choose a PNG, JPEG, or WebP cover up to 10 MB.');
    await getCurrentUser();
    const attributes = await fetchUserAttributes();
    const sub = attributes["sub"];
    const firstName = attributes["custom:firstname"];
    const lastName = attributes["custom:lastname"];

    const presignPayload = {
      sub,
      firstName,
      lastName,
      bookTitle: bookData.title || book.title || "Untitled",
      format: formatType || "General",
      fileName: file.name,
      fileSize: file.size,
      contentType: file.type,
      uploadType: "book",
      ...(isSeries && bookData.series?.trim() ? { series: bookData.series.trim() } : {}),
      ...(isSeries && String(bookData.bookNumber || "").trim()
        ? { bookNumber: String(bookData.bookNumber).trim() }
        : {}),
    };

    const { data: ps } = await axios.post(COVER_PRESIGN_URL, presignPayload, { headers: await authorStorageHeaders() });
    if (!ps?.author?.url || !ps?.master?.url) throw new Error("Presign response incomplete");

    const [authorPut, masterPut] = await Promise.all([
      putToPresigned(file, ps.author),
      putToPresigned(file, ps.master),
    ]);

    const coverImageUrl = `https://bookish-master-data.s3.us-west-2.amazonaws.com/${ps.master.key.split("/").map(encodeURIComponent).join("/")}`;
    return {
      coverImageUrl,
      coverKey: ps.master.key,
      imageEntry: {
        authorBucket: authorPut.bucket,
        authorKey: authorPut.key,
        masterBucket: masterPut.bucket,
        masterKey: masterPut.key,
        url: coverImageUrl,
        filename: file.name,
        contentType: file.type,
        size: file.size,
        uploadedAt: new Date().toISOString(),
      },
    };
  };

  /* ---------- Cover upload (existing) ---------- */
  const handleImageUpload = async (index, files) => {
    if (!begin("Uploading cover…")) return;
    try {
      const file = files?.[0];
      if (!file) return;

      const uploadFormat = formats[index]?.type || formats[0]?.type || "General";
      const { coverImageUrl, coverKey, imageEntry } = await uploadCoverImageAsset({
        file,
        formatType: uploadFormat,
      });

      setFormats((prev) => {
        if (useSameImage) {
          return prev.map((format) => ({
            ...format,
            coverImageUrl,
            displayCoverUrl: coverImageUrl,
            coverKey,
            coverImages: [...(format.coverImages || []), imageEntry],
          }));
        }

        const updated = [...prev];
        const f = { ...(updated[index] || {}) };
        f.coverImageUrl = coverImageUrl;
        f.displayCoverUrl = coverImageUrl;
        f.coverKey = coverKey;
        f.coverImages = [...(f.coverImages || []), imageEntry];
        updated[index] = f;
        return updated;
      });
    } catch (err) {
      console.error("❌ Upload failed", err);
      report(`Failed to upload image: ${err?.response?.data?.error || err.message}`);
    } finally { end(); }
  };

  const handleVariationImageUpload = async (formatIndex, variationIndex, files) => {
    if (!begin("Uploading variation image…")) return;
    try {
      const file = files?.[0];
      if (!file) return;
      const variation = (formats[formatIndex]?.variations || [])[variationIndex] || {};
      if ((variation.images || []).length >= MAX_VARIATION_IMAGES) {
        report(`Variations support up to ${MAX_VARIATION_IMAGES} images.`);
        return;
      }

      const { imageEntry } = await uploadCoverImageAsset({
        file,
        formatType: `${formats[formatIndex]?.type || "Format"}-Variation`,
      });

      setFormats((prev) =>
        prev.map((format, idx) => {
          if (idx !== formatIndex) return format;
          return {
            ...format,
            variations: (format.variations || []).map((item, currentIndex) =>
              currentIndex === variationIndex
                ? {
                    ...item,
                    images: [...(item.images || []), imageEntry].slice(
                    0,
                    MAX_VARIATION_IMAGES
                  ),
                  }
                : item
            ),
          };
        })
      );
    } catch (err) {
      console.error("❌ Variation upload failed", err);
      report(`Failed to upload variation image: ${err?.response?.data?.error || err.message}`);
    } finally { end(); }
  };

  const removeVariationImage = (formatIndex, variationIndex, imageIndex) => {
    setFormats((prev) =>
      prev.map((format, idx) =>
        idx === formatIndex
          ? {
              ...format,
              variations: (format.variations || []).map((variation, currentIndex) =>
                currentIndex === variationIndex
                  ? {
                      ...variation,
                      images: (variation.images || []).filter((_, currentImageIndex) => currentImageIndex !== imageIndex),
                    }
                  : variation
              ),
            }
          : format
      )
    );
  };

  /* ---------- eBook upload (NEW; matches AddBook) ---------- */
  const handleEbookFilePick = (index, file) => {
    if (file && (!/\.(pdf|epub)$/i.test(file.name) || !file.size || file.size > 100 * 1024 * 1024)) { report('Choose a PDF or EPUB up to 100 MB.'); return; }
    setFormats((prev) =>
      prev.map((f, i) =>
        i === index
          ? {
              ...f,
              ebookFile: file || null,
              ebookFilename: file ? file.name : "",
              ebookFilesize: file ? file.size : 0,
              ebookMime: file
                ? file.type || (file.name.toLowerCase().endsWith(".epub") ? "application/epub+zip" : "application/pdf")
                : "",
              ebookUploaded: file ? false : hasPersistedEbookAsset(f),
              isDirect:
                f.type === "eBook" && !file && !hasPersistedEbookAsset(f)
                  ? false
                  : f.isDirect,
            }
          : f
      )
    );
  };

  const uploadEbookForFormat = async (index) => {
    if (!begin("Uploading eBook…")) return;
    try {
      const f = formats[index];
      if (!f || f.type !== "eBook") return;

      const title = (bookData.title || book.title || "").trim();
      if (!title) return report("Enter a Title before uploading the eBook.");
      if (!f.ebookFile) return report("Choose an EPUB or PDF first.");

      await getCurrentUser();
      const attrs = await fetchUserAttributes();
      const userSub = attrs["sub"];
      const first = attrs["custom:firstname"];
      const last = attrs["custom:lastname"];

      const titleKey = toKeySafe(title);
      const ext = f.ebookFilename?.toLowerCase().endsWith(".epub") ? "epub" : "pdf";
      const mime = ext === "epub" ? "application/epub+zip" : "application/pdf";

      const { data } = await axios.post(EBOOK_PRESIGN_URL, {
        userSub,
        first,
        last,
        titleKey, title,
        ext,
        mime, size: f.ebookFile.size,
      }, { headers: await authorStorageHeaders() });
      if (!data?.uploadUrl || !data?.key) throw new Error("Presign missing uploadUrl/key");

      await putToPresigned(f.ebookFile, { url: data.uploadUrl, bucket: BUCKET_NAME, key: data.key }, mime);

      setFormats((prev) =>
        prev.map((it, i) =>
          i === index
            ? {
                ...it,
                ebookS3Key: data.key,
                ebookKey: data.key,
                ebookMime: mime,
                ebookUploaded: true,
                ebookFile: null,
              }
            : it
        )
      );

      report("eBook uploaded. Save your changes to use this file.", "success");
    } catch (err) {
      console.error("❌ eBook upload failed", err);
      report(`Failed to upload eBook: ${err?.response?.data?.error || err.message}`);
    } finally { end(); }
  };

  /* ---------- Save (payload exactly like AddBook, plus ebookS3Key if present) ---------- */
  const handleSave = async (publish = bookData.public) => {
    if (!begin(publish ? 'Saving published book…' : 'Saving draft…')) return;
    let requestStarted = false;
    setIsSaving(true);
    setSaveFeedback(null);

    try {
      await getCurrentUser();
      const attributes = await fetchUserAttributes();
      const sub = attributes["sub"];
      const firstName = attributes["custom:firstname"];
      const lastName = attributes["custom:lastname"];


      const cleanGenres = clamp(dedupeLower(mainGenres), 3);
      const cleanSubgenres = dedupeLower(subgenres);
      const preparedFormats = useSameImage ? buildSharedImageFormats(formats) : formats;
      if (!preparedFormats.length || preparedFormats.length > 4 || new Set(preparedFormats.map(f => f.type)).size !== preparedFormats.length) throw new Error('Choose one to four unique formats.');
      if (publish && (!bookData.blurb.trim() || !cleanGenres.length)) throw new Error('Add a blurb and at least one genre before publishing.');
      if (isSeries && (!bookData.series.trim() || !Number.isSafeInteger(Number(bookData.bookNumber)) || Number(bookData.bookNumber) < 1)) throw new Error('Enter a series name and a positive whole book number.');
      for (const f of preparedFormats) {
        const names = (f.variations || []).map(v => toKeySafe(v.name).toLowerCase());
        if (new Set(names).size !== names.length) throw new Error('Variation names must be unique within each format.');
      }

      const bookPayload = {
        title: (bookData.title || "").trim(),
        tagline: bookData.tagline || "",
        blurb: bookData.blurb || "",
        extrainfo: bookData.extrainfo || "",
        category,
        genres: cleanGenres,
        subgenres: cleanSubgenres,
        public: publish,
        formats: preparedFormats.map((f) => {
          const externalLinks = (f.externalLinks || [])
            .map((link) => ({
              label: (link.label || "").trim(),
              url: (link.url || "").trim(),
            }))
            .filter((link) => link.label || link.url);

          for (const link of externalLinks) {
            if (!link.label || !link.url) {
              throw new Error(`Each retailer link for ${f.type} must include both a label and URL.`);
            }
            if (!isValidUrl(link.url)) {
              throw new Error(`Please enter a valid retailer URL for ${f.type}.`);
            }
          }

          if (publish && !f.isDirect && externalLinks.length === 0) {
            throw new Error(`${f.type} must include at least one retailer link when Sell Direct is unchecked.`);
          }

          const isEbook = f.type === "eBook";
          const hasEbookFile = hasPersistedEbookAsset(f) || !!f.ebookUploaded;
          const normalizedPrice = f.isFree ? 0 : f.price === "" ? "" : Number(f.price);

          if ((publish && f.isDirect && normalizedPrice === "") || (normalizedPrice !== "" && (!Number.isFinite(normalizedPrice) || normalizedPrice < 0))) {
            throw new Error(`${f.type} requires a valid price before saving.`);
          }

          const base = {
            type: f.type,
            isFree: !!f.isFree,
            extrainfo: f.extrainfo || "",
            price: normalizedPrice,
            isDirect: !!f.isDirect,
            directShipping: !!f.isDirect,
            externalLinks,
            coverImages: f.coverImages || [],
            coverImageUrl: f.coverImageUrl || "",
            coverKey: f.coverKey || "",
            shipping: normalizeShipping(f.shipping),
          };

          const cleanedVariations = (f.variations || []).map((variation) => {
            const cleanedName = (variation.name || "").trim();
            const cleanedPrice = variation.price === "" ? "" : Number(variation.price);
            const useBaseQuantity = variation.useBaseQuantity !== false;
            const ownQuantity = variation.unlimited ? null : variation.quantity === "" ? "" : Number(variation.quantity);

            if (!cleanedName) throw new Error(`${f.type} variation names are required.`);
            if ((publish && cleanedPrice === "") || (cleanedPrice !== "" && (!Number.isFinite(cleanedPrice) || cleanedPrice < 0))) throw new Error(`${cleanedName} needs a valid variation price.`);
            if (!useBaseQuantity && ownQuantity !== null && ((publish && ownQuantity === "") || (ownQuantity !== "" && (!Number.isSafeInteger(ownQuantity) || ownQuantity < 0)))) {
              throw new Error(`${cleanedName} needs a quantity when base inventory is disabled.`);
            }

            const cleanedVariation = {
              name: cleanedName,
              extrainfo: variation.extrainfo || "",
              price: cleanedPrice,
              useBaseQuantity,
              images: (Array.isArray(variation.images) ? variation.images : [])
                .slice(0, MAX_VARIATION_IMAGES)
                .map((image) => ({
                  url: image?.url || "",
                  filename: image?.filename || image?.fileName || "",
                  authorBucket: image?.authorBucket || "",
                  authorKey: image?.authorKey || "",
                  masterBucket: image?.masterBucket || "",
                  masterKey: image?.masterKey || "",
                  key: image?.key || image?.masterKey || "",
                }))
                .filter((image) => image.url),
            };

            if (!useBaseQuantity) cleanedVariation.quantity = ownQuantity;
            return cleanedVariation;
          });

          if (publish && !hasValidDirectShipping(f)) {
            throw new Error("Weight and package dimensions are required for direct shipping.");
          }

          if (!isEbook && f.isDirect) {
            const normalizedQuantity = f.unlimited ? null : f.quantity === "" ? "" : Number(f.quantity);
            if (normalizedQuantity !== null && ((publish && normalizedQuantity === "") || (normalizedQuantity !== "" && (!Number.isSafeInteger(normalizedQuantity) || normalizedQuantity < 0)))) {
              throw new Error(`${f.type} requires a quantity when sold directly.`);
            }
            base.quantity = normalizedQuantity;
            base.inventoryPolicy = f.unlimited ? "infinite" : "finite";
          }

          if (cleanedVariations.length) {
            base.variations = cleanedVariations;
          }

          if (isEbook) {
            if (publish && f.isDirect && !hasEbookFile) {
              throw new Error("Please upload your eBook file before enabling direct delivery.");
            }
            if (f.ebookFile) {
              throw new Error("Please click 'Upload eBook' before saving.");
            }
            return {
              ...base,
              isFree: !!f.isFree,
              ebookS3Key: f.ebookS3Key || f.ebookKey || "",
              ebookKey: f.ebookKey || f.ebookS3Key || "",
              ebookFilename: f.ebookFilename || "",
              ebookFilesize: f.ebookFilesize || 0,
            };
          }

          return base;
        }),
        ...(isSeries && bookData.series?.trim() ? { series: bookData.series.trim() } : {}),
        ...(isSeries && String(bookData.bookNumber || "").trim()
          ? { bookNumber: String(bookData.bookNumber).trim() }
          : {}),
      };

      const payload = { action: "save", version: book.version, originalTitle: book.title, sub, firstName, lastName, books: [bookPayload] };

      const headers = await authorStorageHeaders();
      requestStarted = true;
      const response = await axios.post(SAVE_URL, payload, { headers });

      if (response.data?.saved !== true) throw new Error("The save could not be confirmed. Please retry.");
      window.dispatchEvent(new Event('bookish:author-books-saved'));
      setSaveFeedback({
        type: "success",
        message: "Book has been updated.",
      });
      setDirty(false);
      navigate("/authorportal/mainpage?tab=books");
    } catch (err) {
      if (requestStarted && (!err.response || err.response.status >= 500 || err.response.data?.code === 'BOOK_SAVE_PENDING')) setCanResume(true);
      setTimeout(() => feedbackRef.current?.focus(), 0);
      setSaveFeedback({
        type: "error",
        message: err?.response?.data?.error || err.message || "The book could not be saved. Your changes are still here; please retry.",
      });
    } finally {
      end();
    }
  };

  /* ---------- Delete ---------- */
  const handleDelete = async () => {
    if (lock.current) return;
    const confirmed = window.confirm("Permanently delete this book and its stored cover and eBook files? Existing download links may stop working. Unpublish instead to keep the files. This cannot be undone.");
    if (!confirmed || !begin("Deleting book…")) return;
    let requestStarted = false;
    try {
      await getCurrentUser();
      const attrs = await fetchUserAttributes();
      const sub = attrs["sub"];
      const firstName = attrs["custom:firstname"];
      const lastName = attrs["custom:lastname"];

      const payload = {
        action: "delete", version: book.version,
        sub,
        firstName,
        lastName,
        book: {
          title: book.title,
          series: book.series || undefined,
          bookNumber: book.bookNumber || undefined,
        },
      };

      const headers = await authorStorageHeaders();
      requestStarted = true;
      const resp = await axios.post(
        "https://yg2oooqsm0.execute-api.us-west-2.amazonaws.com/default/savebookdata",
        payload,
        { headers }
      );
      if (resp.data?.deleted !== true) throw new Error("The deletion could not be confirmed. Please retry.");
      setDirty(false);
      navigate("/authorportal/mainpage?tab=books");
    } catch (err) {
      const server = err?.response?.data;
      console.error("❌ Failed to delete book", err, server);
      if (requestStarted && (!err.response || err.response.status >= 500 || server?.code === 'BOOK_SAVE_PENDING')) setCanResume(true);
      report(`Delete failed: ${server?.error || err?.message || "Unknown error"}`);
    } finally { end(); }
  };

  /* ------------------- UI ------------------- */
  return (
    <div className="mx-auto mt-10 w-full max-w-5xl px-4">
      <div className="bg-bookish-cream/40 border border-bookish-purple/20 p-3 sm:p-6 rounded-2xl shadow-sm">
        <div className="bg-white border border-bookish-purple/20 p-3 sm:p-6 rounded-2xl shadow-xl">
        <h1 className="text-3xl font-serif font-bold">Edit Book</h1>
        <span className="text-sm text-bookish-purple/70">
          Update your existing book details below. All previously saved information is preserved unless you change it.
          You can manage ebook delivery, inventory, external links, and publishing status from this page.
        </span>
        <br />

        <div className="my-4 text-bookish-purple">
          <p>{bookData.public ? 'Published' : 'Draft'}</p>
          {dirty && <p role="status">You have unsaved changes.</p>}
          {busy && <p role="status">{busy}</p>}
          {saveFeedback && <p ref={feedbackRef} tabIndex={-1} role={saveFeedback.type === 'error' ? 'alert' : 'status'}>{saveFeedback.message}</p>}
          {canResume && <button disabled={Boolean(busy)} onClick={resume} className="min-h-[44px] underline mr-4">Finish previous book operation</button>}
          <button disabled={Boolean(busy)} onClick={refresh} className="min-h-[44px] underline">Reload saved book</button>
        </div>
        <fieldset ref={formRef} disabled={Boolean(busy)} onChangeCapture={() => setDirty(true)} onClickCapture={e => { if (e.target.closest('button')) setDirty(true); }} className="mt-8 space-y-6 min-w-0">
          {/* Title */}
          <div>
            <label className="block font-semibold">Title:</label>
            <input
              name="title"
              readOnly
              aria-describedby="title-help"
              value={displayLabel(bookData.title)}
              onChange={handleInputChange}
              className="w-full border rounded p-2"
            />
          </div>

<p id="title-help" className="text-sm text-bookish-purple/70">The saved title is fixed here because it identifies your book and its files. Contact support if you need to rename it.</p>
          {/* Series toggle + fields */}
          <label className="flex items-center gap-2 mb-2">
            <input
              type="checkbox"
              checked={isSeries}
              onChange={(e) => {
                const v = e.target.checked;
                setIsSeries(v);
                if (!v) setBookData((b) => ({ ...b, series: "", bookNumber: "" }));
              }}
            />
            <span>This book is part of a series</span>
          </label>

          {isSeries && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-1">Series Name</label>
                <input
                  type="text"
                  value={displayLabel(bookData.series)}
                  onChange={(e) => setBookData((b) => ({ ...b, series: e.target.value }))}
                  placeholder="e.g., The Magic of Omneth"
                  className="w-full rounded-md border px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Book Number in Series</label>
                <input
                  type="number"
                  min="1"
                  inputMode="numeric"
                  value={bookData.bookNumber}
                  onChange={(e) => setBookData((b) => ({ ...b, bookNumber: e.target.value }))}
                  placeholder="e.g., 1"
                  className="w-full rounded-md border px-3 py-2"
                />
              </div>
            </div>
          )}

          {/* Tagline */}
          <div>
            <label className="block font-semibold">Tagline:</label>
            <input
              name="tagline"
              value={bookData.tagline}
              onChange={handleInputChange}
              className="w-full border rounded p-2"
            />
          </div>

          {/* Blurb */}
          <div>
            <label className="block font-semibold">Blurb:</label>
            <textarea
              name="blurb"
              value={bookData.blurb}
              onChange={handleInputChange}
              className="w-full border rounded p-2"
              rows={5}
            />
          </div>
          {/* Extra Info */}
          <div>
            <label className="block font-semibold">Extra Info (optional):</label>
            <textarea
              name="extrainfo"
              value={bookData.extrainfo}
              onChange={handleInputChange}
              className="w-full border rounded p-2"
              rows={3}
            />
          </div>

          {/* Category */}
          <div>
            <label className="block font-semibold">Category:</label>
            <div className="mt-2 flex flex-col sm:flex-row gap-4">
              {["Fiction", "Non-Fiction"].map((opt) => (
                <label key={opt} className="flex items-center gap-2">
                  <input type="radio" checked={category === opt} onChange={() => onChangeCategory(opt)} />
                  <span>{opt}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Main Genre (max 3) */}
          <div>
            <label className="block font-semibold">Main Genre (max 3):</label>
            <select
              value=""
              onChange={(e) => addMainGenre(e.target.value)}
              className="border p-2 rounded w-full mt-2"
            >
              <option value="">Select a main genre…</option>
              {availableMain.map((g) => (
                <option key={g} value={g} disabled={mainGenres.includes(g) || mainGenres.length >= 3}>
                  {displayLabel(g)}
                </option>
              ))}
            </select>

            <div className="mt-2 flex flex-wrap gap-2">
              {mainGenres.map((g) => {
                const active = selectedMainForSubs === g;
                return (
                  <span
                    key={g}
                    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm border transition ${
                      active
                        ? "bg-bookish-purple text-white border-bookish-purple"
                        : "bg-white text-bookish-purple border-bookish-purple/20"
                    }`}
                  >
                    <button type="button" onClick={() => setSelectedMainForSubs(g)} className="min-h-[44px]" title="Use this for subgenres">
                      {displayLabel(g)}
                    </button>
                    <button
                      type="button"
                      onClick={() => removeMainGenre(g)}
                      className={`ml-1 rounded-full px-1 ${active ? "hover:bg-white/20" : "hover:bg-bookish-purple/10"}`}
                      aria-label={`Remove ${displayLabel(g)}`}
                      title="Remove"
                    >
                      ×
                    </button>
                  </span>
                );
              })}
            </div>
          </div>

          {/* Subgenres */}
          <div>
            <label className="block font-semibold">Subgenre (optional):</label>
            {selectedMainForSubs ? (
              <>
                <div className="text-sm text-bookish-purple/70 mt-1">
                  Subgenres for: <span className="font-medium">{displayLabel(selectedMainForSubs)}</span>
                </div>
                {subOptions.length > 0 ? (
                  <select
                    value=""
                    onChange={(e) => addSubgenre(e.target.value)}
                    className="border p-2 rounded w-full mt-2"
                  >
                    <option value="">Add a subgenre…</option>
                    {subOptions.map((sg) => (
                      <option key={sg} value={sg} disabled={subgenres.includes(sg)}>
                        {displayLabel(sg)}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="mt-2 text-sm text-bookish-purple/60">No predefined subgenres for this main genre.</div>
                )}
              </>
            ) : (
              <div className="mt-2 text-sm text-bookish-purple/60">Select a main genre above to choose subgenres.</div>
            )}

            <div className="mt-2 flex flex-wrap gap-2">
              {subgenres.map((sg) => (
                <button
                  key={sg}
                  type="button"
                  onClick={() => removeSubgenre(sg)}
                  className="px-3 py-1.5 min-h-[44px] rounded-full text-sm border border-bookish-purple/20 bg-white hover:border-bookish-purple/40"
                  title="Remove"
                >
                  {displayLabel(sg)} <span className="ml-1">×</span>
                </button>
              ))}
            </div>
          </div>


          {/* Formats */}
          <hr className="my-6 border-bookish-purple/20" />
          <h2 className="text-xl font-bold mb-2">Formats</h2>

          <label className="mb-4 flex items-center gap-2 rounded-lg border border-bookish-purple/20 bg-white px-4 py-3">
            <input
              type="checkbox"
              checked={useSameImage}
              onChange={(e) => setUseSameImage(e.target.checked)}
            />
            <span className="font-medium text-bookish-purple">Use same image for all formats</span>
          </label>

          {useSameImage && (
            <div className="mb-4 rounded-xl border border-bookish-purple/20 bg-bookish-cream/40 p-4 shadow-sm">
              <label className="block font-semibold">Shared Cover Image:</label>
              {(() => {
                const sharedSource = formats.find((format) => resolveCoverUrl(format));
                const sharedPreview = sharedSource ? resolveCoverUrl(sharedSource) : "";

                return sharedPreview ? (
                  <img
                    src={sharedPreview}
                    alt="Shared cover"
                    className="mb-2 h-48 rounded bg-white object-contain"
                    onError={(e) => {
                      e.currentTarget.src = PLACEHOLDER;
                    }}
                  />
                ) : (
                  <img src={PLACEHOLDER} alt="Shared cover not uploaded" className="mb-2 h-48 rounded object-contain" />
                );
              })()}
              <input
                aria-label="Shared cover image"
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={(e) => handleImageUpload(0, e.target.files)}
                className="block w-full text-sm file:mr-3 file:rounded-xl file:border-0 file:bg-bookish-purple file:px-4 file:py-2 file:text-white hover:file:brightness-110"
              />
            </div>
          )}

          {formats.map((format, idx) => {
            const isEbook = format.type === "eBook";
            const hasExistingEbookAsset = !format.ebookFile && (hasPersistedEbookAsset(format) || !!format.ebookUploaded);
            const hasSelectedEbookFile = !!format.ebookFile;
            const isDirectDisabled = isEbook && !hasExistingEbookAsset;
            const purchaseLinks = format.externalLinks || [];
            const showQuantity = format.isDirect && !isEbook;
            const showShipping = shouldShowShipping(format);
            const shipping = normalizeShipping(format.shipping);
            const showShippingError = !hasValidDirectShipping(format);
            const showNoPurchaseOptions = !format.isDirect && purchaseLinks.length === 0;
            const canShowVariations = !!format.isDirect;
            const formatVariations = format.variations || [];

            return (
              <div key={idx} className="border border-bookish-purple/20 p-4 rounded-xl mb-4 bg-bookish-cream/40 shadow-sm">
                {!useSameImage && (
                  <div className="mb-2">
                    <label className="block">Cover Image:</label>
                    {(() => {
                      const url = resolveCoverUrl(format);
                      return url ? (
                        <img
                          src={url}
                          alt="Cover"
                          className="h-48 mb-2 object-contain bg-white rounded"
                          loading="lazy"
                          decoding="async"
                          onError={(e) => (e.currentTarget.src = PLACEHOLDER)}
                        />
                      ) : (
                        <img src={PLACEHOLDER} alt={`${format.type} cover not uploaded`} className="mb-2 h-48 rounded object-contain" />
                      );
                    })()}
                    <input aria-label={`${format.type} cover image`} type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => handleImageUpload(idx, e.target.files)} className="block w-full text-sm file:mr-3 file:rounded-xl file:border-0 file:bg-bookish-purple file:px-4 file:py-2 file:text-white hover:file:brightness-110" />
                  </div>
                )}

                {useSameImage && (
                  <div className="mb-3 rounded-lg border border-bookish-purple/10 bg-white px-3 py-2 text-sm text-bookish-purple/70">
                    Using shared cover image for this format.
                  </div>
                )}

                <label>Type:</label>
                <select
                  value={format.type}
                  onChange={(e) => handleFormatChange(idx, "type", e.target.value)}
                  className="w-full border rounded p-2 mb-2"
                >
                  {formatOptions.map((opt) => (
                    <option key={opt}>{opt}</option>
                  ))}
                </select>

                {isEbook && (
                  <p className="mb-3 text-sm text-bookish-purple/70">Digital format — no shipping required</p>
                )}

                {/* eBook upload block */}
                {isEbook && (
                  <div className="mt-3 rounded-lg border border-bookish-purple/20 bg-white p-3">
                    <label className="block text-sm font-medium text-bookish-royal mb-2">
                      eBook File (.epub or .pdf)
                    </label>

                    <input
                      type="file"
                      accept=".epub,.pdf,application/epub+zip,application/pdf"
                      onChange={(e) => handleEbookFilePick(idx, e.target.files?.[0] || null)}
                      className="block w-full text-sm file:mr-3 file:rounded-xl file:border-0 file:bg-bookish-purple file:px-4 file:py-2 file:text-white hover:file:brightness-110"
                    />

                    {hasSelectedEbookFile ? (
                      <div className="mt-2 text-sm text-bookish-royal/80">
                        Selected: <span className="font-medium">{format.ebookFilename}</span>
                        <span className="ml-2 text-bookish-royal/60">
                          ({formatBytes(format.ebookFilesize)})
                        </span>
                      </div>
                    ) : hasExistingEbookAsset ? (
                      <div className="mt-2 text-sm text-green-700">
                        Existing upload: <span className="font-medium">saved eBook on file</span>
                      </div>
                    ) : (
                      <p className="mt-2 text-xs text-bookish-royal/60">
                        Accepted: .epub or .pdf • Maximum 100 MB
                      </p>
                    )}

                    <div className="mt-3 flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => uploadEbookForFormat(idx)}
                        disabled={!format.ebookFile}
                        className={`inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm text-white ${
                          !format.ebookFile
                            ? "bg-bookish-purple/60 cursor-not-allowed"
                            : "bg-bookish-purple hover:brightness-110"
                        }`}
                        title={!bookData.title && !book.title ? "Enter a Title first" : ""}
                      >
                        <Upload className="h-4 w-4" />
                        Upload eBook
                      </button>

                      {hasExistingEbookAsset ? (
                        <span className="inline-flex items-center gap-1 text-green-700 text-sm">
                          <CheckCircle2 className="h-4 w-4" />
                          Uploaded
                        </span>
                      ) : hasSelectedEbookFile ? (
                        <span className="inline-flex items-center gap-1 text-amber-700 text-sm">
                          <AlertCircle className="h-4 w-4" />
                          Not uploaded yet
                        </span>
                      ) : null}
                    </div>
                  </div>
                )}

                <div className="mt-3">
                  <label className="block font-semibold mt-3">Price:</label>
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-2">
                    <input
                      type="text"
                      inputMode="decimal"
                      value={format.isFree ? "0.00" : (format.price ?? "")}
                      onChange={(e) => {
                        let v = e.target.value.replace(/[^\d.]/g, "");
                        const parts = v.split(".");
                        if (parts.length > 2) v = parts[0] + "." + parts.slice(1).join("");
                        if (parts[1]?.length > 2) v = parts[0] + "." + parts[1].slice(0, 2);
                        handleFormatChange(idx, "price", v);
                      }}
                      onBlur={(e) => {
                        const v = e.target.value;
                        if (!format.isFree && v !== "") {
                          const n = Number(v);
                          if (Number.isFinite(n)) handleFormatChange(idx, "price", n.toFixed(2));
                        }
                      }}
                      disabled={format.isFree}
                      required
                      className={`w-full border p-2 ${format.isFree ? "bg-gray-100 text-gray-500" : ""}`}
                      placeholder={format.isFree ? "Free" : "0.00"}
                      title="Enter a price like 4.99"
                    />

                    {isEbook && (
                      <label className="flex items-center gap-2 text-sm whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={!!format.isFree}
                          onChange={(e) => handleFormatChange(idx, "isFree", e.target.checked)}
                        />
                        Free
                      </label>
                    )}
                  </div>
                </div>
                <div className="mt-3">
                  <label className="block font-semibold">Extra Information (optional):</label>
                  <textarea
                    value={format.extrainfo || ""}
                    onChange={(e) => handleFormatChange(idx, "extrainfo", e.target.value)}
                    className="w-full border rounded p-2"
                    rows={3}
                    placeholder="Signed copy, includes goodies, special packaging, etc."
                  />
                </div>
                <div className="mt-3 rounded-lg border border-bookish-purple/20 bg-white p-3">
                  <label className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={!!format.isDirect}
                      onChange={(e) => handleFormatChange(idx, "isDirect", e.target.checked)}
                      disabled={isDirectDisabled}
                      className="mt-1"
                    />
                    <span>
                      <span className="block font-semibold">
                        {isEbook
                          ? "I have uploaded this eBook and confirm it will be delivered directly to readers after purchase."
                          : "I will sell and fulfill this format directly (shipping, delivery, etc.)"}
                      </span>
                      <span className="mt-1 block text-sm text-bookish-purple/70">
                        {isEbook
                          ? "This file will be automatically delivered to the reader after purchase."
                          : "You are responsible for shipping and fulfillment for this format."}
                      </span>
                    </span>
                  </label>
                  {isDirectDisabled && (
                    <p className="mt-2 text-sm text-amber-700">
                      Please upload your eBook file before enabling direct delivery.
                    </p>
                  )}
                </div>

                {showQuantity && (
                  <>
                    <label className="mt-3 flex gap-2"><input type="checkbox" checked={Boolean(format.unlimited)} onChange={e => handleFormatChange(idx, 'unlimited', e.target.checked)} />Unlimited stock</label>
                    <label className="mt-3 block font-semibold">Quantity:</label>
                    <input
                      type="number"
                      min="0"
                      value={format.quantity}
                      disabled={format.unlimited}
                      onChange={(e) => handleFormatChange(idx, "quantity", e.target.value)}
                      className="w-full border p-2"
                    />
                  </>
                )}

                {canShowVariations && (
                  <div className="mt-4 rounded-lg border border-bookish-purple/20 bg-white p-4">
                    <div className="flex items-center justify-between gap-3">
                      <h3 className="font-semibold">Variations</h3>
                      <button
                        type="button"
                        onClick={() => toggleVariationsSection(idx)}
                        className="text-sm text-bookish-purple hover:underline"
                      >
                        {format.showVariations ? "Hide Variations" : "Add Variations"}
                      </button>
                    </div>

                    {format.showVariations && (
                      <div className="mt-3 space-y-3">
                        {formatVariations.map((variation, variationIdx) => (
                          <div key={`${idx}-variation-${variationIdx}`} className="rounded-lg border border-bookish-purple/10 p-3">
                            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                              <div>
                                <label className="block text-sm font-semibold">Name</label>
                                <input
                                  type="text"
                                  value={displayLabel(variation.name || "")}
                                  onChange={(e) => handleVariationChange(idx, variationIdx, "name", e.target.value)}
                                  className="w-full rounded border p-2"
                                  placeholder="Sprayed Edges"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-semibold">Price</label>
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={variation.price ?? ""}
                                  onChange={(e) => handleVariationChange(idx, variationIdx, "price", e.target.value)}
                                  className="w-full rounded border p-2"
                                  placeholder="0.00"
                                />
                              </div>
                            </div>
                            <div className="mt-3">
                              <label className="block text-sm font-semibold">Extra Information</label>
                              <textarea
                                value={variation.extrainfo || ""}
                                onChange={(e) =>
                                  handleVariationChange(idx, variationIdx, "extrainfo", e.target.value)
                                }
                                className="w-full rounded border p-2"
                                rows={3}
                                placeholder="Sprayed gold edges, signed, no goodies included."
                              />
                            </div>
                            <label className="mt-3 flex items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                checked={variation.useBaseQuantity !== false}
                                onChange={(e) => handleVariationChange(idx, variationIdx, "useBaseQuantity", e.target.checked)}
                              />
                              Use same inventory as base format
                            </label>

                            {variation.useBaseQuantity === false && (
                              <div className="mt-2">
                                <label className="flex gap-2"><input type="checkbox" checked={Boolean(variation.unlimited)} onChange={e => handleVariationChange(idx, variationIdx, 'unlimited', e.target.checked)} />Unlimited variation stock</label>
                                <label className="block text-sm font-semibold">Quantity</label>
                                <input
                                  type="number"
                                  min="0"
                                  value={variation.quantity ?? ""}
                                  disabled={variation.unlimited}
                                  onChange={(e) => handleVariationChange(idx, variationIdx, "quantity", e.target.value)}
                                  className="w-full rounded border p-2"
                                />
                              </div>
                            )}

                            <div className="mt-3">
                              <label className="block text-sm font-semibold">
                                Variation Images (max {MAX_VARIATION_IMAGES})
                              </label>
                              <input
                                type="file"
                                accept="image/png,image/jpeg,image/webp"
                                onChange={(e) => handleVariationImageUpload(idx, variationIdx, e.target.files)}
                                disabled={(variation.images || []).length >= MAX_VARIATION_IMAGES}
                              />
                              {(variation.images || []).length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-2">
                                  {variation.images.map((image, imageIdx) => (
                                    <div key={`${idx}-${variationIdx}-${imageIdx}`} className="relative">
                                      <img src={image.url} alt={displayLabel(variation.name || "Variation")} className="h-16 w-16 rounded object-cover" />
                                      <button
                                        type="button"
                                        onClick={() => removeVariationImage(idx, variationIdx, imageIdx)}
                                        className="absolute -right-2 -top-2 rounded-full bg-white px-1 text-xs text-red-500 shadow"
                                      >
                                        ×
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>

                            <button
                              type="button"
                              onClick={() => removeVariation(idx, variationIdx)}
                              className="mt-3 text-sm text-red-500 hover:underline"
                            >
                              Remove Variation
                            </button>
                          </div>
                        ))}

                        <button
                          type="button"
                          onClick={() => addVariation(idx)}
                          className="inline-flex items-center gap-1 rounded-md border border-bookish-purple/20 px-3 py-2 text-sm text-bookish-purple hover:bg-bookish-purple/5"
                        >
                          <Plus className="h-4 w-4" /> Add Variation
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {showShipping && (
                  <div className="mt-4 rounded-lg border border-bookish-purple/20 bg-white p-4">
                    <h3 className="font-semibold">Shipping</h3>
                    <div className="mt-4 space-y-2">
                      <div>
                        <label className="block text-sm font-semibold">Weight (lbs)</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          required={showShipping}
                          value={shipping.weight}
                          onChange={(e) => handleFormatChange(idx, "shipping.weight", e.target.value)}
                          placeholder="e.g. 1.2"
                          className="w-full border p-2"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold">Dimensions (inches)</label>
                        <div className="mt-1 grid grid-cols-1 gap-2 sm:grid-cols-3">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={shipping.length}
                            onChange={(e) => handleFormatChange(idx, "shipping.length", e.target.value)}
                            placeholder="Length"
                            aria-label={`${format.type} package length in inches`}
                            className="w-full border p-2"
                          />
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={shipping.width}
                            onChange={(e) => handleFormatChange(idx, "shipping.width", e.target.value)}
                            placeholder="Width"
                            aria-label={`${format.type} package width in inches`}
                            className="w-full border p-2"
                          />
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={shipping.height}
                            onChange={(e) => handleFormatChange(idx, "shipping.height", e.target.value)}
                            placeholder="Height"
                            aria-label={`${format.type} package height in inches`}
                            className="w-full border p-2"
                          />
                        </div>
                      </div>

                      <label className="flex items-start gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={!!shipping.freeShipping}
                          onChange={(e) => handleFormatChange(idx, "shipping.freeShipping", e.target.checked)}
                          className="mt-1"
                        />
                        <span>
                          <span className="block font-semibold">Free Shipping</span>
                          <span className="block text-bookish-purple/70">
                            Offer this item with free shipping to the reader. A shipping address will still be collected and you can still purchase a shipping label.
                          </span>
                        </span>
                      </label>

                      <label className="flex items-start gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={!!shipping.mediaMailEligible}
                          onChange={(e) => handleFormatChange(idx, "shipping.mediaMailEligible", e.target.checked)}
                          disabled={!!shipping.hasExtras}
                          className="mt-1"
                        />
                        <span>Eligible for USPS Media Mail (books only)</span>
                      </label>

                      <label className="flex items-start gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={!!shipping.hasExtras}
                          onChange={(e) => handleFormatChange(idx, "shipping.hasExtras", e.target.checked)}
                          className="mt-1"
                        />
                        <span>Includes extras (stickers, inserts, swag, etc.)</span>
                      </label>

                      {shipping.mediaMailEligible && shipping.hasExtras && (
                        <p className="text-xs text-amber-700">
                          Media Mail cannot include extras. This option will be disabled at checkout.
                        </p>
                      )}
                      {showShippingError && (
                        <p className="text-xs text-red-600">
                          Weight and package dimensions are required for direct shipping.
                        </p>
                      )}
                    </div>
                  </div>
                )}

                <div className="mt-4 rounded-lg border border-bookish-purple/20 bg-white p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <label className="block font-semibold">Retailer Links</label>
                      <p className="text-sm text-bookish-purple/70">
                        Add optional fallback retailers when selling direct, or required retailer links for retail-only formats.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => addExternalLink(idx)}
                      className="inline-flex items-center gap-1 rounded-md border border-bookish-purple/20 px-3 py-2 text-sm text-bookish-purple hover:bg-bookish-purple/5"
                    >
                      <Plus className="h-4 w-4" /> Add Retailer
                    </button>
                  </div>

                  <div className="mt-3 space-y-3">
                    {purchaseLinks.map((link, linkIdx) => {
                      const showError = (!format.isDirect || link.label || link.url) && (!link.label || !link.url);
                      const showUrlError = !!link.url && !isValidUrl(link.url);
                      return (
                        <div key={`${idx}-${linkIdx}`} className="grid grid-cols-1 gap-3 rounded-lg border border-bookish-purple/10 p-3 md:grid-cols-[1fr_1fr_auto]">
                          <div>
                            <label className="block text-sm font-semibold">Label</label>
                            <input
                              type="text"
                              placeholder="Amazon"
                              value={link.label}
                              onChange={(e) => handleExternalLinkChange(idx, linkIdx, "label", e.target.value)}
                              className={`w-full rounded border p-2 ${showError && !link.label ? "border-red-400" : ""}`}
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-semibold">URL</label>
                            <input
                              type="url"
                              placeholder="https://example.com/book"
                              value={link.url}
                              onChange={(e) => handleExternalLinkChange(idx, linkIdx, "url", e.target.value)}
                              className={`w-full rounded border p-2 ${showError && !link.url ? "border-red-400" : showUrlError ? "border-red-400" : ""}`}
                            />
                          </div>

                          <div className="flex items-end">
                            <button
                              type="button"
                              onClick={() => removeExternalLink(idx, linkIdx)}
                              className="text-sm text-red-500 hover:underline"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {showNoPurchaseOptions && (
                    <p className="mt-3 text-sm text-amber-700">No purchase options available</p>
                  )}
                </div>

                {formats.length > 1 && (
                  <button type="button" onClick={() => removeFormat(idx)} className="text-red-500 mt-2">
                    Remove Format
                  </button>
                )}
              </div>
            );
          })}

          <div className="mt-2">
            <button
              type="button"
              onClick={addFormat}
              disabled={formats.length >= 4}
              className="inline-flex items-center rounded-md border border-bookish-purple/30 bg-bookish-cream/40 px-4 py-2 text-sm font-medium text-bookish-purple transition hover:border-bookish-purple hover:bg-bookish-cream"
            >
              + Add Format
            </button>
          </div>

          {/* Save / Delete / Back */}
          {/* ---- BUTTONS + PUBLISH/UNPUBLISH ---- */}
          <div className="flex flex-col gap-3 mt-6">
            {/* Top row: Save / Delete / Back */}
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => handleSave(bookData.public)}
                disabled={isSaving}
                className={`px-4 py-2 rounded-md text-white ${
                  isSaving
                    ? "cursor-not-allowed bg-bookish-purple/60"
                    : "bg-bookish-purple hover:bg-bookish-purpleAccent"
                }`}
              >
                {isSaving ? "Saving..." : "Save Changes"}
              </button>

              <button onClick={handleDelete} className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700">
                Delete Book
              </button>

              <button onClick={leave} className="px-5 py-2 rounded-md border border-bookish-purple/20 hover:bg-bookish-purple/5">
                Back to My Books
              </button>
            </div>

            <p className="text-sm text-bookish-purple/70">Publishing or unpublishing also saves your current edits.</p>
            {hasInvalidDirectShipping && <p className="text-sm text-bookish-purple">Direct physical formats need weight and dimensions before publishing. You can still save a draft.</p>}
            <button onClick={() => handleSave(!bookData.public)} className="min-h-[44px] rounded-lg bg-bookish-purple text-white px-4 py-2">{bookData.public ? 'Unpublish and Save Draft' : 'Publish and Save'}</button>

          </div>

          </fieldset>
      </div>
    </div>
  </div>
  );
};

export default BookDetails;
